import { portalAPI, portalURL } from '@/helpers/environmentHelper';
import { pushLuceraNotification } from '@/helpers/notificationsHelper';
import { logToServer } from '@/services/LogService';
import { useAppDispatch, useAppSelector } from '@/state/hooks';
import { selectCredentials, selectCurrentAccount, setLuceraJwt } from '@/state/reducers/authSlice';
import { getBlotterTradesThunk, getLuceraBlotterItemsThunk } from '@/state/reducers/blotterSlice';
import { pushPriceChange } from '@/state/reducers/chartDataSlice';
import { addBidAsk, selectActiveMarketPair } from '@/state/reducers/marketPairSlice';
import { LumeFxOrder } from '@/types/lumeFx';
import axios from 'axios';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import useWebSocket from 'react-use-websocket';

export interface LuceraContext {
    jwt?: string;
    isLoadingLucera: LuceraLoadingState;
}

export interface LuceraProviderProps {
    children: React.ReactNode;
}

export type LuceraLoadingState = 'idle' | 'pending' | 'success' | 'error';

const luceraURL = window.config?.lucera?.websocket;
const livePricesType = window.config.modules.chart?.livePricesType || 'bid';

export const instruments = [
    'ADA/EUR',
    'ADA/GBP',
    'ADA/USD',
    'ALG/EUR',
    'ALG/GBP',
    'ALG/USC',
    'ALG/USD',
    'APE/USD',
    'ARB/USD',
    'ATM/EUR',
    'ATM/GBP',
    'ATM/USD',
    'AUD/CAD',
    'AUD/CHF',
    'AUD/DKK',
    'AUD/HKD',
    'AUD/JPY',
    'AUD/NZD',
    'AUD/SGD',
    'AUD/USD',
    'AVE/USD',
    'AVX/EUR',
    'AVX/GBP',
    'AVX/USC',
    'AVX/USD',
    'AXI/AUD',
    'AXS/USD',
    'BAT/USD',
    'BCH/AUD',
    'BCH/BTC',
    'BCH/EUR',
    'BCH/GBP',
    'BCH/JPY',
    'BCH/USC',
    'BCH/USD',
    'BCH/UST',
    'BNB/EUR',
    'BNB/GBP',
    'BNB/USC',
    'BNB/USD',
    'BNK/USD',
    'BTC/AUD',
    'BTC/EUR',
    'BTC/GBP',
    'BTC/JPY',
    'BTC/USC',
    'BTC/USD',
    'BTC/UST',
    'CAD/CHF',
    'CAD/HKD',
    'CAD/JPY',
    'CAD/MXN',
    'CCX/USD',
    'CHF/JPY',
    'CHF/MXN',
    'CHF/NOK',
    'CHF/SEK',
    'CHF/SGD',
    'CHP/USD',
    'CHZ/EUR',
    'CHZ/USD',
    'CMP/EUR',
    'CMP/USD',
    'CNI/USD',
    'COC/USD',
    'COF/USD',
    'COR/USD',
    'CPP/USD',
    'CRV/USC',
    'CRV/USD',
    'CTL/USD',
    'DAI/USD',
    'DJI/USD',
    'DOG/EUR',
    'DOG/GBP',
    'DOG/JPY',
    'DOG/USD',
    'DOT/EUR',
    'DOT/GBP',
    'DOT/USC',
    'DOT/USD',
    'ETH/AUD',
    'ETH/BTC',
    'ETH/EUR',
    'ETH/GBP',
    'ETH/JPY',
    'ETH/USC',
    'ETH/USD',
    'ETH/UST',
    'EUR/AUD',
    'EUR/CAD',
    'EUR/CHF',
    'EUR/CNH',
    'EUR/CZK',
    'EUR/DKK',
    'EUR/GBP',
    'EUR/HKD',
    'EUR/HUF',
    'EUR/ILS',
    'EUR/JPY',
    'EUR/MXN',
    'EUR/NOK',
    'EUR/NZD',
    'EUR/PLN',
    'EUR/RON',
    'EUR/RUB',
    'EUR/SEK',
    'EUR/SGD',
    'EUR/TRY',
    'EUR/USD',
    'EUR/ZAR',
    'FNG/USD',
    'FRI/EUR',
    'GAL/USD',
    'GBP/AUD',
    'GBP/CAD',
    'GBP/CHF',
    'GBP/CZK',
    'GBP/DKK',
    'GBP/HKD',
    'GBP/HUF',
    'GBP/JPY',
    'GBP/MXN',
    'GBP/NOK',
    'GBP/NZD',
    'GBP/PLN',
    'GBP/SEK',
    'GBP/SGD',
    'GBP/TRY',
    'GBP/USD',
    'GBP/ZAR',
    'GEM/EUR',
    'GLD/USD',
    'GLM/EUR',
    'GLM/GBP',
    'GLM/USC',
    'GLM/USD',
    'GRT/USD',
    'HKD/JPY',
    'HSH/HKD',
    'HSI/HKD',
    'HYP/EUR',
    'HYP/GBP',
    'HYP/USD',
    'IBX/EUR',
    'INI/USD',
    'ITA/EUR',
    'JPU/JPY',
    'JPY/MXN',
    'KSM/EUR',
    'KSM/GBP',
    'KSM/USC',
    'KSM/USD',
    'LNK/AUD',
    'LNK/EUR',
    'LNK/GBP',
    'LNK/JPY',
    'LNK/USC',
    'LNK/USD',
    'LRC/USD',
    'LTC/AUD',
    'LTC/BTC',
    'LTC/EUR',
    'LTC/GBP',
    'LTC/JPY',
    'LTC/USC',
    'LTC/USD',
    'LTC/UST',
    'MAN/USD',
    'MEW/USD',
    'MGE/USD',
    'MXN/JPY',
    'NAT/USD',
    'NDM/USD',
    'NED/EUR',
    'NER/EUR',
    'NER/GBP',
    'NER/USC',
    'NER/USD',
    'NOK/JPY',
    'NOK/SEK',
    'NZD/CAD',
    'NZD/CHF',
    'NZD/JPY',
    'NZD/SGD',
    'NZD/USD',
    'PAL/USD',
    'PEP/USD',
    'PLA/USD',
    'PNU/USD',
    'POL/EUR',
    'POL/GBP',
    'POL/USC',
    'POL/USD',
    'RSI/USD',
    'SEK/JPY',
    'SGD/JPY',
    'SGP/SGD',
    'SHB/EUR',
    'SHB/GBP',
    'SHB/USC',
    'SHB/USD',
    'SKY/USD',
    'SLV/USD',
    'SMI/CHF',
    'SND/USD',
    'SOL/EUR',
    'SOL/GBP',
    'SOL/USC',
    'SOL/USD',
    'SOY/USD',
    'SPL/USD',
    'SPM/USD',
    'STX/EUR',
    'SUG/USD',
    'SUI/EUR',
    'SUS/USD',
    'SWD/SEK',
    'TPX/JPY',
    'TRP/USD',
    'TRX/EUR',
    'TRX/GBP',
    'TRX/USD',
    'TST/USD',
    'TWN/USD',
    'UKI/GBP',
    'UKO/USD',
    'UNI/EUR',
    'UNI/USD',
    'USC/AUD',
    'USC/CAD',
    'USC/CHF',
    'USC/EUR',
    'USC/GBP',
    'USC/JPY',
    'USC/MXN',
    'USC/NZD',
    'USC/SGD',
    'USC/USD',
    'USC/UST',
    'USD/AED',
    'USD/BRL',
    'USD/CAD',
    'USD/CHF',
    'USD/CLP',
    'USD/CNH',
    'USD/CNY',
    'USD/CZK',
    'USD/DKK',
    'USD/HKD',
    'USD/HUF',
    'USD/IDR',
    'USD/ILS',
    'USD/INR',
    'USD/JPY',
    'USD/KRW',
    'USD/MXN',
    'USD/MYR',
    'USD/NOK',
    'USD/PHP',
    'USD/PLN',
    'USD/RON',
    'USD/RUB',
    'USD/SEK',
    'USD/SGD',
    'USD/THB',
    'USD/TRY',
    'USD/TWD',
    'USD/ZAR',
    'USI/USD',
    'USO/USD',
    'UST/AED',
    'UST/AUD',
    'UST/CAD',
    'UST/CHF',
    'UST/EUR',
    'UST/GBP',
    'UST/USC',
    'UST/USD',
    'VIX/USD',
    'WHT/USD',
    'WIF/USD',
    'XAG/AUD',
    'XAG/USD',
    'XAU/AUD',
    'XAU/BTC',
    'XAU/EUR',
    'XAU/USD',
    'XAU/XAG',
    'XDC/EUR',
    'XDC/GBP',
    'XDC/USC',
    'XLM/EUR',
    'XLM/GBP',
    'XLM/USC',
    'XLM/USD',
    'XPD/USD',
    'XPT/USD',
    'XRP/AUD',
    'XRP/BTC',
    'XRP/EUR',
    'XRP/GBP',
    'XRP/JPY',
    'XRP/USC',
    'XRP/USD',
    'XRP/UST',
    'XTZ/EUR',
    'XTZ/USD',
    'XUT/USD',
    'ZAR/JPY',
    'ZEC/USD'
];

const LuceraContext = createContext({} as LuceraContext);
export const useLucera = () => useContext(LuceraContext);
export const LuceraProvider = (props: LuceraProviderProps) => {
    const dispatch = useAppDispatch();
    const credentials = useAppSelector(selectCredentials);
    const currentAccount = useAppSelector(selectCurrentAccount);
    const activePair = useAppSelector(selectActiveMarketPair);

    const [jwt, setJWT] = useState<string>();
    const [isLoadingLucera, setIsLoadingLucera] = useState<LuceraLoadingState>('idle');

    const onFetchJWT = useCallback(async () => {
        if (credentials && !jwt) {
            setIsLoadingLucera('pending');
            try {
                if (portalURL) {
                    const response = await axios.post(portalAPI('/api/auth/authenticate-via-lucera'), {
                        // username: credentials?.username,
                        // celer_auth_token: credentials?.authToken
                    });
                    dispatch(setLuceraJwt(response.data));
                    setIsLoadingLucera('success');
                    setJWT(response.data);
                    logToServer('info', `User [${credentials?.username}] has authenticated via Lucera`);
                    dispatch(getLuceraBlotterItemsThunk()).then(() => dispatch(getBlotterTradesThunk()));
                    return response.data;
                }
            } catch (err: any) {
                setIsLoadingLucera('error');
                logToServer(
                    'info',
                    `User [${credentials?.username}] has failed to authenticate via Lucera. Error: ${err?.message}`
                );
            }
        } else if (!credentials) {
            setJWT(undefined);
        }
    }, [credentials, jwt]);

    const didUnmount = useRef(false);

    const wsURL = useMemo(() => {
        if (credentials && jwt) return luceraURL;
        else return null;
    }, [credentials, jwt]);

    const options = useMemo(
        () => ({
            reconnectAttempts: 5,
            retryOnError: false,
            reconnectInterval: (attempt) => Math.min(1000 * 2 ** attempt, 10000),
            // shouldReconnect: (event) => event.code !== 1000,
            onOpen: () => {
                console.log('Connected');
                sendJsonMessage({
                    msg_type: 'logon',
                    authorization: `Bearer ${jwt}`
                });

                try {
                    instruments.forEach((instrument) => {
                        const req = {
                            msg_type: 'orderbook_request',
                            action: 'subscribe',
                            symbol: instrument
                        };
                        sendJsonMessage(req);
                    });
                } catch (err) {
                    console.error('Error processing instruments:', err);
                }
            },
            onError: (e) => {
                console.error('WebSocket error:', e);
            },
            onClose: (e) => {
                console.log('WebSocket closed:', e);
            }
        }),
        [jwt]
    );

    const websocket = useWebSocket<any>(wsURL, options);

    const { readyState, lastMessage, lastJsonMessage, getWebSocket, sendJsonMessage } = websocket;
    const priceChangeFilter = useCallback((pair: string) => pair === activePair?.celer, [activePair]);

    const callback = useCallback(
        async (marketSub) => {
            if (marketSub.symbol) {
                const market = marketSub.symbol;
                const bestBid = marketSub['bid'][0][0];
                const bestAsk = marketSub['ask'][0][0];

                if (bestBid && bestAsk) {
                    const timestamp = new Date().getTime();
                    if (priceChangeFilter(market)) {
                        dispatch(
                            pushPriceChange({
                                symbol: market,
                                price: bestBid,
                                timestamp
                            })
                        );
                    }
                    dispatch(
                        addBidAsk({
                            celerPair: market,
                            bid: bestBid,
                            ask: bestAsk,
                            timestamp,
                            settlementDate: marketSub.settlementDate
                        })
                    );
                }
                // dispatch(updateOrderBook({ priceEvent: marketSub }));
            }
        },
        [priceChangeFilter]
    );

    useEffect(() => {
        // dispatch(setNopPositionStatus(ReadyState[readyState] as keyof typeof ReadyState));
    }, [readyState]);

    useEffect(() => {
        if (lastJsonMessage && !lastJsonMessage.isHeartBeat) {
            if (lastJsonMessage.msg_type === 'order_status') {
                pushLuceraNotification({ latestEvent: lastJsonMessage as LumeFxOrder, dispatch });
            }
            if (lastJsonMessage.msg_type === 'orderbook_response') {
                callback(lastJsonMessage);
            }
        }
    }, [lastJsonMessage, dispatch]);

    useEffect(() => {
        return () => {
            didUnmount.current = true;
        };
    }, []);

    useEffect(() => {
        onFetchJWT();
    }, [credentials]);

    const value = useMemo<LuceraContext>(
        () => ({
            jwt,
            isLoadingLucera,
            readyState,
            lastJsonMessage,
            getWebSocket
        }),
        [jwt, isLoadingLucera, readyState, lastJsonMessage, getWebSocket]
    );

    return <LuceraContext.Provider value={value}>{props.children}</LuceraContext.Provider>;
};
